import { useAppStore } from '@store/appStore';

/**
 * Mobile History Button Component
 */
interface MobileHistoryButtonProps {
  direction: 'back' | 'forward';
  onClick: () => void;
  disabled: boolean;
}

function MobileHistoryButton({ direction, onClick, disabled }: MobileHistoryButtonProps) {
  const icon = direction === 'back' ? (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M15 18l-6-6 6-6"/>
    </svg>
  ) : (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 18l6-6-6-6"/>
    </svg>
  );

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={`${direction === 'back' ? 'Go back' : 'Go forward'} in navigation history`}
      style={{
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        background: disabled 
          ? 'rgba(255, 255, 255, 0.05)' 
          : 'rgba(255, 255, 255, 0.15)',
        border: '1.5px solid rgba(255, 255, 255, 0.4)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.3 : 1,
        transition: 'all 0.2s ease',
        color: 'white',
        padding: 0,
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
        }
      }}
    >
      {icon}
    </button>
  );
}

/**
 * Mobile History Buttons Component
 * Simplified navigation controls for mobile devices
 */
export function MobileHistoryButtons() {
  const navigationHistory = useAppStore(state => state.navigationHistory);
  const goBackInHistory = useAppStore(state => state.goBackInHistory);
  const goForwardInHistory = useAppStore(state => state.goForwardInHistory);

  const canGoBack = navigationHistory.currentIndex > 0;
  const canGoForward = navigationHistory.currentIndex < navigationHistory.steps.length - 1;

  const handleBack = () => {
    if (canGoBack) {
      goBackInHistory();
    }
  };

  const handleForward = () => {
    if (canGoForward) {
      goForwardInHistory();
    }
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '80px', // Above section drawer handle (drawer starts at bottom:0)
      right: '20px',
      display: 'flex',
      gap: '12px',
      zIndex: 1000001, // Above section drawer (999999)
      pointerEvents: 'auto',
    }}>
      <MobileHistoryButton
        direction="back"
        onClick={handleBack}
        disabled={!canGoBack}
      />
      <MobileHistoryButton
        direction="forward"
        onClick={handleForward}
        disabled={!canGoForward}
      />
    </div>
  );
}